// Documentation for google.accounts.id can be found at:
// https://developers.google.com/identity/gsi/web/reference/js-reference
import { useCallback, useEffect, useState } from 'react'
import Script from 'next/script'
import { googleOneTapAuth } from '@mc/authentication'
import { getEnvVar } from '@mc/client-env'
import { usePrimaryCta } from '@mc/growth'
import { reportError } from '@mc/next-newrelic'
import { trackFlyoutContinueClicked } from '../../events/socialVendorLogInInitiated'
import {
  trackFlyoutHidden,
  trackFlyoutTappedOutside,
  trackFlyoutViewed,
} from '../../events/socialVendorLogin'
import { setGoogleOneTapQuizSource } from '../../hooks/googleOneTapQuizEntry'
import { trackEV } from '../../hooks/useExperiment'

export const GoogleFlyout = ({
  isModalBanned,
  saveCloseAction,
}: {
  isModalBanned: boolean
  saveCloseAction: (modal: 'google-flyout' | 'pop-up') => void
}) => {
  const { ctaHref } = usePrimaryCta()
  const [state, setState] = useState<
    | 'loading' // script is loading
    | 'loaded' // script loaded successfully
    | 'initialized' // google library initialized
  >('loading')

  const loaded = useCallback(() => {
    if (state === 'loading') setState('loaded')
  }, [state])

  const init = useCallback(() => {
    if (
      typeof window === 'undefined' ||
      window?.google?.accounts?.id === undefined
    ) {
      return
    }

    const CLIENT_ID = getEnvVar('GOOGLE_OAUTH2_WEB_CLIENT_ID') as string

    window.google.accounts.id.initialize({
      client_id: CLIENT_ID,
      context: 'use',
      itp_support: true,
      callback: ({ credential }) => {
        trackFlyoutContinueClicked()
        googleOneTapAuth({
          credential,
          redirectTo: ctaHref,
          onLogIn: () => setGoogleOneTapQuizSource({ source: 'log_in_flyout' }),
        })
      },
    })

    setState('initialized')
  }, [ctaHref])

  const show = useCallback(() => {
    if (
      typeof window === 'undefined' ||
      window?.google?.accounts?.id === undefined
    ) {
      return
    }

    trackFlyoutViewed()
    trackEV()

    window.google.accounts.id.prompt((notification) => {
      const momentType = notification.getMomentType()

      if (momentType === 'skipped') {
        const skippedReason = notification.getSkippedReason() // will be deprecated when FedCM is totally migrated
        switch (skippedReason) {
          case 'tap_outside':
            trackFlyoutTappedOutside()
            break
          case 'user_cancel': // this notification isn't triggered by chrome, it triggers uknowkn_reason for this case
            trackFlyoutHidden()
            saveCloseAction('google-flyout')
            break
          case 'unknown_reason' as string:
            saveCloseAction('google-flyout')
            trackFlyoutHidden()
            break
          default:
            break
        }
      }
    })
  }, [saveCloseAction])

  useEffect(() => {
    if (state === 'loaded') init()

    if (state === 'initialized' && !isModalBanned) show()
  }, [init, show, state, isModalBanned])

  return (
    <Script
      src='https://accounts.google.com/gsi/client'
      onLoad={loaded}
      onReady={loaded}
      onError={(e) => {
        reportError(e, {
          description: 'There was an error initializing Google One Tap.',
        })
      }}
    />
  )
}
